import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { closeVoting, deleteVote, invalidateVoting } from '../../helpers/api/case';
import { INVALID_INPUT_TEXT } from '../../helpers/constants';
import { formatVote } from '../../helpers/tools';
import { H2 } from '../../layout/theme/components';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import TextButton from '../../shared/components/buttons/TextButton';
import DenyBubble from '../../shared/components/DenyBubble';
import DropdownList from '../../shared/components/DropdownList';
import Textarea from '../../shared/components/forms/Textarea';
import FullModalContainer from '../../shared/components/FullModalContainer';
import Modal from '../../shared/components/Modal';
import Table from '../../shared/components/Table';
import VotingContainer from '../sounding/VotingContainer';
import ButtonsWrapper from './components/ButtonsWrapper';
import ClosingModalContent from './components/ClosingModalContent';
import ErrorComponent from './components/ErrorComponent';
import VoteStatus from './components/VoteStatus';

const VotingListContainer = ({
  caseId,
  votesList,
  resolutionOpened,
  canVoteByUser,
  canCancelResolution,
  canCloseResolution,
  getCasePreview,
  resolutionTitle,
  userRole
}) => {
  const [openVotingModal, votingModalToggle] = useState(false);
  const [showClosingModal, closingModalToggle] = useState(false);
  const [showCancelModal, cancelModalToggle] = useState(false);
  const [cancelText, setCancelText] = useState('');
  const [user, setUser] = useState('');
  const [userApartment, setUserApartment] = useState('');
  const [deleteResolutionId, setDeleteResolutionId] = useState(null);
  const [error, setError] = useState(null);

  const toggleVotingModal = (userId, userApartment) => {
    userId && setUser(userId);
    userApartment && setUserApartment(userApartment);

    votingModalToggle(!openVotingModal);
    if (userId === '') getCasePreview();
  };
  const toggleClosingModal = () => closingModalToggle(!showClosingModal);

  const closingVoting = () => {
    closeVoting(caseId)
      .then(() => closingModalToggle(false))
      .then(() => getCasePreview())
      .catch(error => setError(error));
  };

  const toggleCancelModal = () => {
    cancelModalToggle(!showCancelModal);
    setCancelText('');
    setError(null);
  };
  const handleCancelText = value => {
    setCancelText(value);
    setError(null);
  };

  const cancelVoting = () => {
    invalidateVoting(caseId, cancelText)
      .then(() => closingModalToggle(false))
      .then(() => getCasePreview())
      .catch(error => setError(error));
  };

  const toggleDeleteModal = resolutionId => {
    setDeleteResolutionId(resolutionId || null);
    setError(null);
  };

  const confirmDeleteVote = () => {
    deleteVote(caseId, deleteResolutionId)
      .then(() => setDeleteResolutionId(null))
      .then(() => getCasePreview())
      .catch(error => setError(error));
  };

  const columns = [
    { id: 'apartment', label: 'Lokal' },
    { id: 'name', label: 'Imię, nazwisko' },
    { id: 'share', label: 'Udział', tooltip: 'Udział we wspólnocie' },
    { id: 'vote', label: 'Głos' }
  ];
  const renderVoteStatus = (share, item, users, showInvalid) => (
    <VoteStatus
      users={users}
      voteInvalid={showInvalid ? share.vote_invalid : false}
      shareVote={share.vote}
      shareVotedBy={share.voted_by}
      onVote={userId => toggleVotingModal(userId, item.display_name)}
      onDelete={toggleDeleteModal}
      canVote={canVoteByUser && resolutionOpened}
    />
  );

  /**
   * Co-owners get a row each only when the vote is recorded per user. Snapshots
   * taken before that keep one vote on the share, so splitting would repeat it.
   */
  const getCoOwners = share => {
    const voters = share.users.filter(user => user.id);
    const hasVotesPerUser = voters.every(user => user.vote !== undefined);

    return hasVotesPerUser && voters.length > 1 ? voters : null;
  };

  const createRow = item => {
    const multiShares = item.shares.length > 1 ? true : false;

    if (multiShares) {
      return {
        apartment: <b>{item.display_name}</b>,
        name: '',
        share: <b>{formatVote(item.apartment_share)}</b>,
        subrows: item.shares.reduce((subrows, share) => {
          const coOwners = getCoOwners(share);

          if (!coOwners) {
            subrows.push({
              apartment: `${share.apartment_share} w lokalu`,
              name: share.users.map(user => (
                <div key={user.id}>{user.display_name}</div>
              )),
              share: formatVote(share.share),
              vote: renderVoteStatus(share, item, share.users, true)
            });

            return subrows;
          }

          coOwners.forEach((user, position) => {
            subrows.push(
              position === 0
                ? {
                    apartment: {
                      content: `${share.apartment_share} w lokalu`,
                      rowSpan: coOwners.length
                    },
                    name: user.display_name,
                    share: {
                      content: formatVote(share.share),
                      rowSpan: coOwners.length
                    },
                    vote: renderVoteStatus(share, item, [user], true)
                  }
                : {
                    spanned: ['apartment', 'share'],
                    name: user.display_name,
                    vote: renderVoteStatus(share, item, [user], false)
                  }
            );
          });

          return subrows;
        }, [])
      };
    } else {
      const rowData = item.shares[0];
      const coOwners = rowData ? getCoOwners(rowData) : null;

      if (coOwners) {
        const [firstOwner, ...otherOwners] = coOwners;

        return {
          apartment: {
            content: <b>{item.display_name}</b>,
            rowSpan: coOwners.length
          },
          name: firstOwner.display_name,
          share: {
            content: <b>{formatVote(rowData.share)}</b>,
            rowSpan: coOwners.length
          },
          vote: renderVoteStatus(rowData, item, [firstOwner], true),
          subrows: otherOwners.map(user => ({
            spanned: ['apartment', 'share'],
            name: user.display_name,
            vote: renderVoteStatus(rowData, item, [user], false)
          }))
        };
      }

      const name = rowData
        ? rowData.users.map(user => (
            <div key={user.id}>{user.display_name}</div>
          ))
        : '';
      return {
        apartment: <b>{item.display_name}</b>,
        name: name,
        share: (
          <b>
            {rowData
              ? formatVote(rowData.share)
              : formatVote(item.apartment_share)}
          </b>
        ),
        vote: rowData && renderVoteStatus(rowData, item, rowData.users, true)
      };
    }
  };

  return (
    <>
      <DropdownList header={'Lista głosowania'} open={userRole !== 'ROLE_USER'}>
        <Table
          columns={columns}
          rows={votesList.map(item => createRow(item))}
        />
      </DropdownList>
      {resolutionOpened && (
        <ButtonsWrapper>
          {canCloseResolution && (
            <PrimaryButton onClick={toggleClosingModal}>
              ZAMKNIJ GŁOSOWANIE
            </PrimaryButton>
          )}
          {canCancelResolution && (
            <TextButton onClick={toggleCancelModal}>
              UNIEWAŻNIJ UCHWAŁĘ
            </TextButton>
          )}
        </ButtonsWrapper>
      )}
      {openVotingModal && (
        <Modal closeModal={() => toggleVotingModal()}>
          <VotingContainer
            caseId={caseId}
            headerTitle={userApartment}
            userId={user}
            callback={() => toggleVotingModal('', '')}
            caseStatus="resolution_in_progress"
            resolution
            inModal
          />
          {error && <ErrorComponent errorStatus={error.status} />}
        </Modal>
      )}
      {showClosingModal && (
        <Modal closeModal={toggleClosingModal}>
          <DenyBubble
            leftButton="ANULUJ"
            leftClick={toggleClosingModal}
            rightButton="ZATWIERDŹ"
            rightClick={() => closingVoting()}
          >
            <ClosingModalContent />
          </DenyBubble>
          {error && <ErrorComponent errorStatus={error.status} />}
        </Modal>
      )}
      {showCancelModal && (
        <Modal closeModal={toggleCancelModal} isWide>
          <FullModalContainer
            onClick={value => cancelVoting(value)}
            buttonText={'UNIEWAŻNIJ'}
            header={'Unieważnienie uchwały'}
            caption={`Tytuł: ${resolutionTitle}`}
          >
            <Textarea
              placeholder="Wpisz powód unieważnienia"
              value={cancelText}
              changeHandler={value => handleCancelText(value)}
              invalidText={INVALID_INPUT_TEXT}
            />
            {error && <ErrorComponent errorStatus={error.status} />}
          </FullModalContainer>
        </Modal>
      )}
      {deleteResolutionId && (
        <Modal closeModal={() => toggleDeleteModal()}>
          <DenyBubble
            leftButton="NIE"
            leftClick={() => toggleDeleteModal()}
            rightButton="TAK"
            rightClick={() => confirmDeleteVote()}
          >
            <H2>Czy na pewno chcesz usunąć ten głos z systemu?</H2>
          </DenyBubble>
          {error && <ErrorComponent errorStatus={error.status} />}
        </Modal>
      )}
    </>
  );
};

VotingListContainer.propTypes = {
  resolutionOpened: PropTypes.bool.isRequired,
  canCloseResolution: PropTypes.bool,
  canCancelResolution: PropTypes.bool,
  votesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      display_name: PropTypes.string,
      voted: PropTypes.number
    })
  ),
  resolutionTitle: PropTypes.string
};
VotingListContainer.defaultProps = {
  votesList: null,
  resolutionTitle: ''
};

const mapStateToProps = state => ({
  canVoteByUser: state.user.accessControlList.data ? state.user.accessControlList.data.user.voting_by_user : false,
  canCancelResolution: state.user.accessControlList.data ? state.user.accessControlList.data.user.resolution_cancel : false,
  canCloseResolution: state.user.accessControlList.data ? state.user.accessControlList.data.user.resolution_closing : false,
  userRole: state.user.profile.data ? state.user.profile.data.role : null
});

export default connect(mapStateToProps)(VotingListContainer);
